/**
 * Remix button visibility for Malleable Content. Canonical deny list and show rules.
 * Use with block-controls activeNode (or a full PM node) and these filters in both edit and view.
 */

import { isNodeEmpty } from '@atlaskit/editor-common/utils';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

/** Node types that get the Ask Rovo button on their floating toolbar (media nodes). */
export const ASK_ROVO_MEDIA_NODE_TYPES: readonly string[] = ['mediaSingle', 'media'] as const;

/** Node types we never show Remix for. */
export const REMIX_DENY_LIST_V1: readonly string[] = [
	'mediaSingle',
	'mediaGroup',
	'media',
	'image',
	'extension',
	'bodiedExtension',
	'multiBodiedExtension',
	'embedCard',
	'blockCard',
	'inlineCard',
	'bodiedSyncBlock',
] as const;

const DENY_SET = new Set<string>(REMIX_DENY_LIST_V1);

/** True if this node type is in the remix deny list (restricted). */
export const isRemixRestrictedNodeType = (nodeType: string): boolean => DENY_SET.has(nodeType);

/**
 * Reusable filter for remix button visibility. Use with block-controls activeNode
 * (nodeType / rootNodeType) or a full PM node in both edit and view.
 *
 * - Deny-list is always applied when we have a type (never show Remix on media, extensions, etc.).
 * - When a full node is provided, we also require the node to be non-empty (no Remix on empty blocks).
 * - When only nodeType is available (e.g. from activeNode in view mode), we only apply the deny-list.
 */
export const shouldShowRemixForSelectionContext = (context: {
	node?: PMNode;
	nodeType?: string;
}): boolean => {
	const nodeType = context.node?.type?.name ?? context.nodeType;
	if (nodeType === undefined) {
		return false;
	}
	if (isRemixRestrictedNodeType(nodeType)) {
		return false;
	}
	// With full node, also require non-empty (e.g. don't show Remix on empty paragraphs)
	if (context.node !== undefined) {
		return !isNodeEmpty(context.node);
	}
	return true;
};
